import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { DefaultNotFound, DefaultRouteError } from "@/components/RouteFallbacks";
import { PlayerProfileCard } from "@/components/PlayerProfileCard";
import { useSession } from "@/lib/user-data";
import { PALS } from "@/lib/pals-data";
import { PLAYABLE_CARDS, searchOfficialCards } from "@/lib/tcg-cards";
import {
  deleteMyProfile,
  getMyProfile,
  listPlayerProfiles,
  saveMyProfile,
  type MyProfile,
} from "@/lib/player-profiles.functions";
import {
  HANDLE_PATTERN,
  PLAYSTYLES,
  suggestHandle,
  type PlayerProfile,
  type Playstyle,
} from "@/lib/player-profiles-shared";

const URL = "https://palstars.com/players";

const FAQ = [
  {
    q: "What is a Palworld TCG player profile?",
    a: "A Palworld TCG player profile is a public Pal Stars page at /players/<your-name> that shows your trainer name, the way you collect, your region, your favourite Pal and card, and the cards you want or have spare. It is the page you share when you want another collector to know who they are trading with before an offer is sent.",
  },
  {
    q: "Do I need an account to browse Palworld TCG player profiles?",
    a: "No. The player directory and every published profile are public and crawlable, so anyone can read them without signing in. A free Pal Stars account is only needed to publish your own profile or to send a trade offer.",
  },
  {
    q: "What information is shown on my public profile?",
    a: "Only what you type in: display name, profile URL, short bio, region, playstyle, favourite Pal and favourite card, plus counts and codes from your want-list listings. Your email, account ID, binder, collection values and Palsino balance are never published.",
  },
  {
    q: "How do wants and spares appear on a player profile?",
    a: "Your profile reads your active want-list listings. Every card you mark as wanted or as a spare on the want-list board appears on your profile by exact printed card code, so a visitor can see at a glance whether you hold the card they need.",
  },
  {
    q: "Can I hide or delete my Palworld TCG player profile?",
    a: "Yes. Untick \"publish\" to take the profile out of the directory instantly, or delete it outright from the same editor. An unpublished profile is not readable by visitors and is dropped from the sitemap.",
  },
];

export const Route = createFileRoute("/players/")({
  loader: () => listPlayerProfiles(),
  errorComponent: DefaultRouteError,
  notFoundComponent: DefaultNotFound,
  head: () => ({
    meta: [
      { title: "Palworld TCG Player Profiles & Trainers | Pal Stars" },
      {
        name: "description",
        content:
          "Browse public Palworld TCG player profiles: trainer names, playstyles, regions, favourite Pals and cards, plus the cards each collector wants or has spare to trade.",
      },
      { property: "og:title", content: "Palworld TCG Player Profiles" },
      {
        property: "og:description",
        content:
          "Public Palworld card game trainer directory — see who collects what, who is open to trades, and which cards they need.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Player Profiles", "/players"],
      ]),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SpeakableSpecification",
          cssSelector: [".aeo-answer"],
        }),
      },
    ],
  }),
  component: PlayersPage,
});

const EMPTY: MyProfile = {
  handle: "",
  displayName: "",
  bio: "",
  region: "",
  playstyle: "collector",
  favoritePal: "",
  favoriteCard: "",
  tradeReady: true,
  published: true,
};

function ProfileEditor({ onSaved }: { onSaved: () => void }) {
  const saveFn = useServerFn(saveMyProfile);
  const loadFn = useServerFn(getMyProfile);
  const deleteFn = useServerFn(deleteMyProfile);

  const [form, setForm] = useState<MyProfile>(EMPTY);
  const [existing, setExisting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const mine = (await loadFn()) as MyProfile | null;
        if (mine) {
          setForm(mine);
          setExisting(true);
        }
      } catch {
        /* no profile yet */
      }
    })();
  }, [loadFn]);

  const set = <K extends keyof MyProfile>(key: K, value: MyProfile[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const cardSuggestions = useMemo(() => {
    const q = form.favoriteCard.trim();
    return q.length >= 2 ? searchOfficialCards(q, 6) : PLAYABLE_CARDS.slice(0, 30);
  }, [form.favoriteCard]);

  const submit = async () => {
    const handle = form.handle || suggestHandle(form.displayName);
    if (!HANDLE_PATTERN.test(handle)) {
      setNotice("Your profile URL must be 3-24 lowercase letters, numbers or dashes.");
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await saveFn({
        data: {
          handle,
          displayName: form.displayName.trim() || "Trainer",
          bio: form.bio.trim(),
          region: form.region.trim(),
          playstyle: form.playstyle,
          favoritePal: form.favoritePal.trim(),
          favoriteCard: form.favoriteCard.trim(),
          tradeReady: form.tradeReady,
          published: form.published,
        },
      });
      setForm((f) => ({ ...f, handle }));
      setExisting(true);
      setNotice(`Profile saved — it is live at /players/${handle}.`);
      onSaved();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That profile could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const drop = async () => {
    setBusy(true);
    try {
      await deleteFn({});
      setForm(EMPTY);
      setExisting(false);
      setNotice("Profile deleted.");
      onSaved();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That profile could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 glass rounded-2xl p-5">
      <h2 className="text-xl font-bold">{existing ? "Edit your player profile" : "Create your player profile"}</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Trainer / display name</span>
          <input
            value={form.displayName}
            onChange={(e) => set("displayName", e.target.value)}
            maxLength={40}
            placeholder="Palpagos Collector"
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Profile URL (/players/…)</span>
          <input
            value={form.handle}
            onChange={(e) => set("handle", e.target.value.toLowerCase())}
            maxLength={24}
            placeholder={suggestHandle(form.displayName) || "palpagos-collector"}
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Playstyle</span>
          <select
            value={form.playstyle}
            onChange={(e) => set("playstyle", e.target.value as Playstyle)}
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {PLAYSTYLES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label} — {p.blurb}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Region (optional)</span>
          <input
            value={form.region}
            onChange={(e) => set("region", e.target.value)}
            maxLength={40}
            placeholder="EU, UK, US-West…"
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Favourite Pal (optional)</span>
          <input
            value={form.favoritePal}
            onChange={(e) => set("favoritePal", e.target.value)}
            list="profile-pal-names"
            maxLength={60}
            placeholder="Anubis"
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </label>
        <datalist id="profile-pal-names">
          {PALS.slice(0, 300).map((p) => (
            <option key={p.slug} value={p.name} />
          ))}
        </datalist>
        <label className="text-sm">
          <span className="block text-xs text-muted-foreground">Favourite card code (optional)</span>
          <input
            value={form.favoriteCard}
            onChange={(e) => set("favoriteCard", e.target.value)}
            list="profile-card-codes"
            maxLength={24}
            placeholder="EBP01-001"
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
          />
        </label>
        <datalist id="profile-card-codes">
          {cardSuggestions.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name} — {c.code}
            </option>
          ))}
        </datalist>
        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-muted-foreground">Bio (optional, 400 characters)</span>
          <textarea
            value={form.bio}
            onChange={(e) => set("bio", e.target.value)}
            maxLength={400}
            rows={3}
            placeholder="Set collector chasing every Dawn of Palpagos SSP. Happy to trade duplicates by printed code."
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.tradeReady}
            onChange={(e) => set("tradeReady", e.target.checked)}
          />
          Open to trades
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => set("published", e.target.checked)}
          />
          Publish my profile publicly
        </label>
        <button
          onClick={() => void submit()}
          disabled={busy || !form.displayName.trim()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {existing ? "Save profile" : "Create profile"}
        </button>
        {existing && (
          <button
            onClick={() => void drop()}
            disabled={busy}
            className="rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            Delete profile
          </button>
        )}
      </div>
      {notice && <p className="mt-3 text-sm text-primary">{notice}</p>}
    </section>
  );
}

function PlayersPage() {
  const initial = Route.useLoaderData() as PlayerProfile[];
  const { session } = useSession();
  const listFn = useServerFn(listPlayerProfiles);

  const [rows, setRows] = useState<PlayerProfile[]>(initial);
  const [query, setQuery] = useState("");
  const [style, setStyle] = useState<"all" | Playstyle>("all");
  const [tradeOnly, setTradeOnly] = useState(false);

  const refresh = useCallback(async () => {
    setRows((await listFn()) as PlayerProfile[]);
  }, [listFn]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((p) => {
      if (style !== "all" && p.playstyle !== style) return false;
      if (tradeOnly && !p.trade_ready) return false;
      if (!q) return true;
      return (
        p.display_name.toLowerCase().includes(q) ||
        p.handle.includes(q) ||
        p.region.toLowerCase().includes(q) ||
        p.favorite_pal.toLowerCase().includes(q) ||
        p.favorite_card.toLowerCase().includes(q)
      );
    });
  }, [rows, query, style, tradeOnly]);

  const tradeReady = rows.filter((p) => p.trade_ready).length;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Community</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld TCG Player Profiles</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          A Palworld TCG player profile is a public Pal Stars page showing a collector's trainer name, playstyle,
          region, favourite Pal and card, and the exact cards they want or have spare. {rows.length} trainers are
          listed right now and {tradeReady} are open to trades — no account is needed to read a profile.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Post the cards you need on the{" "}
          <Link to="/want-list" className="text-primary hover:underline">
            want list board
          </Link>
          , send a swap from{" "}
          <Link to="/trades" className="text-primary hover:underline">
            card trading
          </Link>
          , and check values on the{" "}
          <Link to="/tcg-price-index" className="text-primary hover:underline">
            TCG price index
          </Link>
          .
        </p>
      </header>

      {session ? (
        <ProfileEditor onSaved={() => void refresh()} />
      ) : (
        <section className="mt-8 glass rounded-2xl p-5 text-sm text-muted-foreground">
          <h2 className="text-xl font-bold text-foreground">Publish your own profile</h2>
          <p className="mt-2">
            Browsing is open to everyone.{" "}
            <Link to="/auth" className="text-primary hover:underline">
              Sign in for a free account
            </Link>{" "}
            to publish a trainer profile with your own URL, playstyle and trade list.
          </p>
        </section>
      )}

      <section className="mt-8">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm flex-1 min-w-[200px]">
            <span className="block text-xs text-muted-foreground">Search trainers</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, region, Pal or card code"
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground">Playstyle</span>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value as "all" | Playstyle)}
              className="mt-1 rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="all">All playstyles</option>
              {PLAYSTYLES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={tradeOnly} onChange={(e) => setTradeOnly(e.target.checked)} />
            Open to trades only
          </label>
        </div>

        <h2 className="mt-6 text-xl font-bold">
          {shown.length} trainer{shown.length === 1 ? "" : "s"}
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {shown.map((p) => (
            <PlayerProfileCard key={p.handle} profile={p} />
          ))}
        </div>
        {shown.length === 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            No profiles match that filter yet. Be the first trainer listed.
          </p>
        )}
      </section>

      <FaqSection items={FAQ} heading="Palworld TCG player profile FAQ" />
    </div>
  );
}
